import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { type Project } from '@/lib/ProjectsManager';
import { useProjectsManager } from '@/hooks/useProjectsManager';
import { ProjectSidebar } from '@/components/ProjectSidebar';
import { Button } from '@/components/ui/button';
import { Menu, ChevronUp, ChevronDown, Compass, LayoutGrid } from 'lucide-react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { AfterHoursLogo } from '@/components/AfterHoursLogo';
import { cn } from '@/lib/utils';

interface AppLayoutProps {
  children: React.ReactNode;
  selectedProject?: Project | null;
  title?: string;
  showSidebar?: boolean;
  headerContent?: React.ReactNode;
  fullWidth?: boolean;
}

export function AppLayout({
  children,
  selectedProject = null,
  showSidebar = true,
  headerContent,
  fullWidth = false
}: AppLayoutProps) {
  const [_projects, setProjects] = useState<Project[]>([]);
  const projectsManager = useProjectsManager();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProjectsDrawerOpen, setIsProjectsDrawerOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const drawerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const loadProjects = useCallback(async () => {
    try {
      await projectsManager.init();
      const projectList = await projectsManager.getProjects();
      setProjects(projectList);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  }, [projectsManager]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Close drawer when clicking outside (desktop)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isProjectsDrawerOpen) return;

      // Don't close drawer if dropdown menu is open
      if (isDropdownOpen) return;

      const target = event.target as HTMLElement;

      // Check if clicking inside drawer or trigger
      if (
        drawerRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) {
        return;
      }

      // Check if any dialog or alert dialog is currently open in the DOM
      // This prevents closing the drawer when interacting with dialogs
      const hasOpenDialog = document.querySelector('[data-state="open"][role="alertdialog"], [data-state="open"][role="dialog"]');
      if (hasOpenDialog) {
        return;
      }

      // Check if click is on a dropdown/portal element (Radix portals content outside drawer)
      const isPortalContent =
        target.closest('[data-radix-popper-content-wrapper]') ||
        target.closest('[data-radix-dropdown-menu-content]') ||
        target.closest('[role="dialog"]') ||
        target.closest('[role="alertdialog"]');

      if (isPortalContent) {
        return;
      }

      setIsProjectsDrawerOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProjectsDrawerOpen, isDropdownOpen]);

  const handleProjectSelect = (project: Project | null) => {
    if (project) {
      setIsMobileSidebarOpen(false);
      setIsProjectsDrawerOpen(false);
      navigate(`/project/${project.id}`);
    } else {
      navigate('/');
    }
  };

  if (isMobile) {
    return (
      <div className="min-h-dvh bg-sidebar">
        {/* Mobile Header */}
        <header className="sticky top-0 z-40 bg-sidebar border-b border-sidebar-border pt-safe">
          <div className="h-12 px-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {showSidebar && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9 w-9 p-0 hover:bg-primary/10"
                  onClick={() => setIsMobileSidebarOpen(true)}
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              )}
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 pr-2 hover:opacity-80 transition-opacity"
              >
                <AfterHoursLogo className="w-6 h-6" />
                <span className="text-lg font-bold text-primary">AfterHours</span>
              </button>
            </div>
            {headerContent}
          </div>
        </header>

        {/* Mobile Sidebar Overlay - positioned below header */}
        {showSidebar && isMobileSidebarOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 top-[calc(env(safe-area-inset-top)+3rem)] z-30 bg-black/50 backdrop-blur-sm"
              onClick={() => {
                // Don't close sidebar if a dialog is open
                const hasOpenDialog = document.querySelector('[data-state="open"][role="alertdialog"], [data-state="open"][role="dialog"]');
                if (!hasOpenDialog) {
                  setIsMobileSidebarOpen(false);
                }
              }}
            />
            {/* Sidebar */}
            <div className="fixed left-0 top-[calc(env(safe-area-inset-top)+3rem)] z-40 h-[calc(100dvh-env(safe-area-inset-top)-3rem)] w-full max-w-sm bg-sidebar border-r border-sidebar-border shadow-xl animate-in slide-in-from-left duration-200">
              <ProjectSidebar
                selectedProject={selectedProject}
                onSelectProject={(project) => {
                  setIsMobileSidebarOpen(false);
                  handleProjectSelect(project);
                }}
                onClose={() => setIsMobileSidebarOpen(false)}
                hideHeader
                isDesktopDrawer
                onDropdownOpenChange={setIsDropdownOpen}
              />
            </div>
          </>
        )}

        <div className={cn(
          "py-6 md:py-8",
          fullWidth ? "w-full" : "container mx-auto px-4"
        )}>
          {children}
        </div>
      </div>
    );
  }

  // Desktop layout - top navigation bar with Projects drawer
  return (
    <div className="min-h-screen flex flex-col bg-sidebar">
      {/* Desktop Top Navigation */}
      {showSidebar && (
        <header className="sticky top-0 z-40 bg-sidebar border-b border-sidebar-border">
          <div className="h-12 px-4 flex items-center gap-1">
            {/* Logo */}
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 pr-2 hover:opacity-80 transition-opacity mr-2"
            >
              <AfterHoursLogo className="w-6 h-6" />
              <span className="text-lg font-bold text-primary">AfterHours</span>
            </button>

            {/* Projects Dropdown Button */}
            <Button
              ref={triggerRef}
              variant={isProjectsDrawerOpen ? "secondary" : "ghost"}
              size="sm"
              className={cn(
                "h-8 gap-1 px-3 text-sm text-muted-foreground hover:text-foreground",
                isProjectsDrawerOpen && "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
              )}
              onClick={() => setIsProjectsDrawerOpen(!isProjectsDrawerOpen)}
            >
              <LayoutGrid className="h-4 w-4" />
              <span>Projects</span>
              {isProjectsDrawerOpen ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </Button>

            {/* Explore Link */}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 px-3 text-sm text-muted-foreground hover:text-foreground"
              onClick={() => navigate('/explore')}
            >
              <Compass className="h-4 w-4" />
              <span>Explore</span>
            </Button>

            {/* Right side - could add more items here */}
            <div className="flex-1" />
            {headerContent}
          </div>
        </header>
      )}

      {/* Projects Drawer - positioned below navbar */}
      {showSidebar && isProjectsDrawerOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 top-12 z-30 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              // Don't close drawer if a dialog is open
              const hasOpenDialog = document.querySelector('[data-state="open"][role="alertdialog"], [data-state="open"][role="dialog"]');
              if (!hasOpenDialog) {
                setIsProjectsDrawerOpen(false);
              }
            }}
          />
          {/* Drawer Panel */}
          <div
            ref={drawerRef}
            className="fixed left-0 top-12 z-40 h-[calc(100vh-3rem)] w-80 border-r border-sidebar-border bg-sidebar shadow-xl animate-in slide-in-from-left duration-200"
          >
            <ProjectSidebar
              selectedProject={selectedProject}
              onSelectProject={(project) => {
                setIsProjectsDrawerOpen(false);
                handleProjectSelect(project);
              }}
              onClose={() => setIsProjectsDrawerOpen(false)}
              hideHeader
              isDesktopDrawer
              onDropdownOpenChange={setIsDropdownOpen}
            />
          </div>
        </>
      )}

      {/* Main Content Area */}
      <div className="flex-1">
        <div className={cn(
          "py-6 md:py-8",
          fullWidth ? "w-full" : "container mx-auto px-4"
        )}>
          {children}
        </div>
      </div>
    </div>
  );
}
